import React, { FC, useEffect, useState, useRef } from "react";
import styles from "./styles.module.scss";
import ExpandableArrow from "./ExpandableArrow";
import CountryList, {
  COUNTRY_QS_KEY,
  countryFlagIconName,
} from "./CountryList";
import LanguageList from "src/components/AICLayout/AICHeader/LocationSelector/LanguageList";
import { KatButton, KatIcon } from "@amzn/katal-react";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { Country } from "src/api/interfaces/country";
import { setRealmBasedOnCountrySelection } from "src/api/interfaces/realm";
import { getUrlParam, updateUrlParams } from "src/utils";
import { usePermissions } from "src/components/Contexts/PermissionsContext";
import { useHistory } from "react-router-dom";

export interface LocationSelectorProps {
  disableCountryChange?: boolean;
  fixedCountry?: string;
  disableUrlSearchParamsUpdate?: boolean;
}

const LocationSelector: FC<LocationSelectorProps> = ({
  disableCountryChange,
  fixedCountry,
  disableUrlSearchParamsUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { availableCountries, selectedCountry, setCountrySelection } =
    useCountrySelection();
  const isFetching = availableCountries.length === 0;

  const history = useHistory();

  const { permissions } = usePermissions();

  const countryCodeParam = getUrlParam(COUNTRY_QS_KEY);

  const shouldUseCountryCodeFromUrl = (
    newlyAvailableCountries: Country[],
    countryCode?: string
  ): boolean => {
    return (
      !!countryCode &&
      newlyAvailableCountries.some(
        (country) => country.countryCode === countryCode
      )
    );
  };

  useEffect(() => {
    const newlyAvailableCountries = Array.from(permissions.keys()).map(
      (countryCode) => {
        return { countryCode };
      }
    );

    const countryCodeFromURL = countryCodeParam;
    let newlySelectedCountry: Country | undefined;

    if (fixedCountry) {
      newlySelectedCountry = { countryCode: fixedCountry };
    } else if (newlyAvailableCountries && newlyAvailableCountries.length) {
      newlySelectedCountry = shouldUseCountryCodeFromUrl(
        newlyAvailableCountries,
        countryCodeFromURL
      )
        ? {
            countryCode: countryCodeFromURL as string,
          }
        : newlyAvailableCountries[0];
    } else {
      newlySelectedCountry = undefined;
    }

    setRealmBasedOnCountrySelection(newlySelectedCountry);

    setCountrySelection({
      availableCountries: newlyAvailableCountries,
      selectedCountry: newlySelectedCountry,
    });

    // setting country code on URL at first loading
    if (
      !disableUrlSearchParamsUpdate &&
      newlySelectedCountry?.countryCode !== countryCodeFromURL &&
      !!newlySelectedCountry
    ) {
      updateUrlParams(
        [[COUNTRY_QS_KEY, newlySelectedCountry?.countryCode]],
        history
      );
    }
  }, [
    countryCodeParam,
    disableUrlSearchParamsUpdate,
    fixedCountry,
    history,
    permissions,
    setCountrySelection,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  const flagIconName = selectedCountry
    ? countryFlagIconName(selectedCountry.countryCode)
    : "minus";

  return (
    <div
      className={styles.location_selector_container}
      ref={containerRef}
      id="document-listing-country-dropdown"
    >
      <div>
        {selectedCountry && <KatIcon name={flagIconName} size={"small"} />}
      </div>
      <ExpandableArrow
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        isFetching={isFetching}
      />
      {isExpanded && (
        <div className={styles.location_dropdown_container}>
          <div className={styles.location_dropdown_inner_container}>
            <div className={styles.country_selector_container}>
              <CountryList
                availableCountries={availableCountries}
                selectedCountry={selectedCountry}
                setCountrySelection={setCountrySelection}
                isFetching={isFetching}
                disableCountryChange={disableCountryChange}
              />
            </div>
            <div className={styles.language_selector_container}>
              <LanguageList />
            </div>
          </div>
          <div className={styles.location_dropdown_footer}>
            <KatButton
              label="Close"
              variant="primary"
              onClick={() => setIsExpanded(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
